import { SensorMetadataDialog } from "./SensorMetadataDialog";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNotifyStatus } from "contexts/status";
import { fetchErrorDetail } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useState } from "react";
import {
  ScrapEyeSensorRead,
  SensorType,
  useCreateSensorMutation,
  useListSensorsQuery,
} from "src/store/api/generatedApi";
import { formatInstallationTimestamp } from "./installationTimestamp";
import {
  SELECTABLE_SENSOR_TYPES,
  SENSOR_TYPE_LABEL_KEYS,
} from "./sensorTypeLabels";

const initialRegistrationState = () => ({
  sensorType: "position_tracker" as SensorType,
  name: "",
  serialNumber: "",
});

export const SensorRegistrySection = () => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();

  const [registration, setRegistration] = useState(initialRegistrationState);
  const [editingMetadata, setEditingMetadata] =
    useState<ScrapEyeSensorRead | null>(null);

  const { data: sensorsResponse, isLoading } = useListSensorsQuery({
    tenantName,
  });
  const [createSensor, { isLoading: isRegistering }] =
    useCreateSensorMutation();

  const sensors = sensorsResponse?.items ?? [];

  const handleRegister = async () => {
    const trimmedName = registration.name.trim();
    const trimmedSerialNumber = registration.serialNumber.trim();

    if (!trimmedName) {
      notifyStatus({ type: "error", text: t("sensorRegisterError") });
      return;
    }

    try {
      await createSensor({
        tenantName,
        scrapEyeSensorCreate: {
          sensor_type: registration.sensorType,
          name: trimmedName,
          serial_number: trimmedSerialNumber || null,
        },
      }).unwrap();
      notifyStatus({ type: "success", text: t("sensorRegisterSuccess") });
      setRegistration(initialRegistrationState());
    } catch (error) {
      notifyStatus({
        type: "error",
        text: fetchErrorDetail(error) ?? t("sensorRegisterError"),
      });
    }
  };

  const renderRow = (sensor: ScrapEyeSensorRead) => (
    <TableRow hover key={sensor.id}>
      <TableCell>{t(SENSOR_TYPE_LABEL_KEYS[sensor.sensor_type])}</TableCell>
      <TableCell>{sensor.name}</TableCell>
      <TableCell>{sensor.serial_number}</TableCell>
      <TableCell>{formatInstallationTimestamp(sensor.created_at)}</TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            onClick={() => {
              setEditingMetadata(sensor);
            }}
          >
            {t("edit")}
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Card variant="outlined">
        <CardHeader title={t("sensorsTitle")} />
        <Divider />
        <CardContent>
          <Stack spacing={3}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                label={t("sensorTypeLabel")}
                value={registration.sensorType}
                onChange={(event) => {
                  setRegistration((state) => ({
                    ...state,
                    sensorType: event.target.value as SensorType,
                  }));
                }}
                sx={{ minWidth: 180 }}
                disabled={isRegistering}
              >
                {SELECTABLE_SENSOR_TYPES.map((sensorType) => (
                  <MenuItem key={sensorType} value={sensorType}>
                    {t(SENSOR_TYPE_LABEL_KEYS[sensorType])}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label={t("sensorNameLabel")}
                value={registration.name}
                onChange={(event) => {
                  setRegistration((state) => ({
                    ...state,
                    name: event.target.value,
                  }));
                }}
                disabled={isRegistering}
              />
              <TextField
                fullWidth
                label={t("serialNumber")}
                value={registration.serialNumber}
                onChange={(event) => {
                  setRegistration((state) => ({
                    ...state,
                    serialNumber: event.target.value,
                  }));
                }}
                disabled={isRegistering}
              />
              <Button
                variant="contained"
                onClick={() => void handleRegister()}
                disabled={isRegistering}
              >
                {t("sensorRegisterButton")}
              </Button>
            </Stack>

            {isLoading ? (
              <Stack alignItems="center">
                <CircularProgress size={24} />
              </Stack>
            ) : sensors.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "fontWeightBold" }}>
                      {t("sensorTypeLabel")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "fontWeightBold" }}>
                      {t("sensorNameLabel")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "fontWeightBold" }}>
                      {t("serialNumber")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "fontWeightBold" }}>
                      {t("sensorCreatedAtLabel")}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>{sensors.map(renderRow)}</TableBody>
              </Table>
            ) : (
              <Typography color="text.secondary">
                {t("sensorsEmpty")}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {editingMetadata != null && (
        <SensorMetadataDialog
          sensor={editingMetadata}
          onClose={() => {
            setEditingMetadata(null);
          }}
        />
      )}
    </>
  );
};
